import React, { useState, useEffect } from 'react';
import Layout from '../layout/Layout';
import { PrivateAxios } from '../../request/axios';
import { toast } from 'react-toastify';
import { FaTrash, FaEdit } from 'react-icons/fa';
import Modal from 'react-modal';
import '../../App.css';

const ModifierINT = () => {
    const [intermediaires, setIntermediaires] = useState([]);
    const [ugList, setUgList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedInt, setSelectedInt] = useState(null);
    const [formData, setFormData] = useState({
        nom: '',
        description: '',
        intitule: '',
        adresse: '',
        code_postal: '',
        ville: '',
        ridet: '',
        tel: '',
        email: ''
    });
    const [ugId, setUgId] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const fetchIntermediaires = async () => {
        try {
            const { data } = await PrivateAxios.get('/admin/intermediaire');
            setIntermediaires(Array.isArray(data?.int) ? data.int : []);
        } catch (err) {
            setError('Erreur lors du chargement des intermédiaires.');
            toast.error('Erreur lors du chargement des intermédiaires.');
        } finally {
            setLoading(false);
        }
    };

    const fetchUGs = async () => {
        try {
            const { data } = await PrivateAxios.get('/admin/ug');
            setUgList(Array.isArray(data?.ug) ? data.ug : []);
        } catch (err) {
            toast.error('Erreur lors du chargement des UG.');
        }
    };


    useEffect(() => {
        fetchIntermediaires();
        fetchUGs();
    }, []);

    const openEditModal = (int) => {
        setSelectedInt(int);
        setFormData({
            nom: int.nom,
            description: int.description,
            intitule: int.intitule || '',
            adresse: int.adresse || '',
            code_postal: int.code_postal || '',
            ville: int.ville || '',
            ridet: int.ridet || '',
            tel: int.tel || '',
            email: int.email || ''
        });
        setUgId(int.ug_id);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedInt(null);
        setFormData({
            nom: '',
            description: '',
            intitule: '',
            adresse: '',
            code_postal: '',
            ville: '',
            ridet: '',
            tel: '',
            email: ''
        });
        setUgId('');
        setIsSubmitting(false);
    };

    // Fonction générique pour gérer les changements de champs
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevFormData) => ({
            ...prevFormData,
            [name]: value
        }));
    };

    const updateIntermediaire = async () => {
        if (!selectedInt) return;

        // Validation des champs
        const requiredFields = ['nom', 'description', 'intitule', 'adresse', 'code_postal', 'ville', 'ridet', 'tel', 'email'];
        for (let field of requiredFields) {
            if (!formData[field]) {
                toast.error(`Le champ ${field.replace('_', ' ')} est obligatoire.`);
                return;
            }
        }

        if (!ugId) {
            toast.error("L'UG d'affectation est obligatoire.");
            return;
        }

        setIsSubmitting(true);

        const payload = {
            ...formData,
            ug_id: ugId,
        };

        try {
            await PrivateAxios.put(`/admin/intermediaire/${selectedInt.id}`, payload);
            toast.success('Intermédiaire modifié avec succès !');
            fetchIntermediaires();
            closeModal();
        } catch (error) {
            toast.error("Erreur lors de la modification de l'intermédiaire.");
            setIsSubmitting(false);
        }
    };

    const deleteIntermediaire = async (intId) => {
        if (!window.confirm('Voulez-vous vraiment supprimer cet intermédiaire ?')) return;

        try {
            await PrivateAxios.delete(`/admin/intermediaire/${intId}`);
            toast.success('Intermédiaire supprimé avec succès !');
            fetchIntermediaires();
        } catch (error) {
            toast.error("Erreur lors de la suppression de l'intermédiaire.");
        }
    };

    const filteredIntermediaires = (intermediaires || []).filter(int =>
        int.nom && int.nom.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Fonction générique pour générer les champs du formulaire
    const renderInputField = (id, label, placeholder, type = 'text', required = true) => (
        <div className="mb-4">
            <label className="block mb-2 text-sm font-bold text-gray-700" htmlFor={id}>
                {label}
            </label>
            <input
                id={id}
                name={id}
                type={type}
                value={formData[id]}
                onChange={(e) => handleInputChange(e)}
                placeholder={placeholder}
                required={required}
                className="w-full px-3 py-2 transition-all duration-300 border rounded-lg shadow-sm focus:outline-none focus:ring focus:ring-blue-500 focus:border-blue-500"
            />
        </div>
    );

    return (
        <Layout>
            <h1 className="mb-6 text-3xl font-bold text-center text-black">Gestion des Intermédiaires</h1>

            {/* Champ de recherche */}
            <div className="flex justify-center mb-4">
                <input
                    type="text"
                    placeholder="Rechercher par nom d'intermédiaire"
                    className="w-1/3 p-2 border border-gray-300 rounded"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            {loading ? (
                <p className="text-center">Chargement des intermédiaires...</p>
            ) : error ? (
                <p className="text-center text-red-500">{error}</p>
            ) : (
                <div className="flex justify-center">
                    <div className="w-full mx-auto overflow-x-auto text-center">
                        <table className="w-full bg-white border border-gray-200 rounded-lg shadow-md">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="px-2 py-2 border">ID</th>
                                    <th className="px-2 py-2 border">Nom</th>
                                    <th className="px-2 py-2 border">Description</th>
                                    <th className='px-2 py-2 border'>UG</th>
                                    <th className="px-2 py-2 border">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredIntermediaires.length > 0 ? (
                                    filteredIntermediaires.map(int => (
                                        <tr key={int.id} className="hover:bg-gray-50">
                                            <td className="px-2 py-2 border">{int.id}</td>
                                            <td className="px-2 py-2 border">{int.nom}</td>
                                            <td className="px-2 py-2 border">{int.description}</td>
                                            <td className='px-2 py-2 border'>{int.ug_nom}</td>
                                            <td className="px-2 py-2 border">
                                                <button
                                                    onClick={() => openEditModal(int)}
                                                    className="mx-2 text-yellow-500 hover:text-yellow-700"
                                                    title="Modifier"
                                                >
                                                    <FaEdit />
                                                </button>

                                                <button
                                                    onClick={() => deleteIntermediaire(int.id)}
                                                    className="mx-2 text-red-500 hover:text-red-700"
                                                    title="Supprimer"
                                                >
                                                    <FaTrash />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="py-4 text-center">Aucun intermédiaire trouvé.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Modale pour modifier l'intermédiaire */}
            <Modal
                isOpen={isModalOpen}
                onRequestClose={closeModal}
                contentLabel="Modifier Intermédiaire"
                ariaHideApp={false}
                style={{
                    content: {
                        top: '50%',
                        left: '50%',
                        right: 'auto',
                        bottom: 'auto',
                        marginRight: '-50%',
                        transform: 'translate(-50%, -50%)',
                        width: '70%', // Largeur ajustée
                        maxWidth: '800px', // Limite de largeur maximale
                        maxHeight: '70vh', // Limite de hauteur maximale
                        padding: '20px',
                        borderRadius: '10px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)', // Meilleure ombre
                        overflowY: 'auto' // Pour le défilement si nécessaire
                    }
                }}
            >
                <h2 className="mb-6 text-xl font-bold text-center">Modifier Intermédiaire</h2>

                <form>
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                        {/* Champ nom */}
                        {renderInputField('nom', "Nom de l'intermédiaire", "Entrez le nom de l'intermédiaire")}

                        {/* Champ description */}
                        <div className="col-span-2">
                            {renderInputField('description', "Description", "Entrez une description", 'textarea')}
                        </div>

                        {/* Autres champs */}
                        {renderInputField('intitule', "Intitulé", "Entrez l'intitulé")}
                        {renderInputField('adresse', "Adresse", "Entrez l'adresse")}
                        {renderInputField('code_postal', "Code Postal", "Entrez le code postal")}
                        {renderInputField('ville', "Ville", "Entrez la ville")}
                        {renderInputField('ridet', "Ridet", "Entrez le RIDET")}
                        {renderInputField('tel', "Téléphone", "Entrez le numéro de téléphone", "number")}
                        {renderInputField('email', "Email", "Entrez l'email", "email")}

                        {/* Champ UG */}
                        <div className="col-span-2">
                            <label className="block mb-2 text-sm font-bold text-gray-700" htmlFor="ugId">
                                UG d'affectation
                            </label>
                            <select
                                id="ugId"
                                value={ugId}
                                onChange={(e) => setUgId(e.target.value)}
                                required
                                className="w-full px-3 py-2 transition-all duration-300 border rounded-lg shadow-sm focus:outline-none focus:ring focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">Sélectionner une UG</option>
                                {ugList.map((ug) => (
                                    <option key={ug.id} value={ug.id}>
                                        {ug.nom}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="flex justify-end mt-6 space-x-4">
                        <button
                            type="button"
                            onClick={closeModal}
                            className="px-4 py-2 text-gray-700 transition-all duration-300 bg-gray-300 rounded hover:bg-gray-400"
                        >
                            Annuler
                        </button>
                        <button
                            type="button"
                            onClick={updateIntermediaire}
                            className={`px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-all duration-300 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'En cours...' : 'Valider'}
                        </button>
                    </div>
                </form>
            </Modal>

        </Layout>
    );
};

export default ModifierINT;
